// components/generic/PremiumFeatureGate/PremiumFeatureGate.tsx
//
// The WHOLE-PAGE lock — for a page whose entire reason to exist is a feature the account's
// plan does not include. Its sibling is `PlanFeatureNotice`, the add-only notice for a page
// that still works and merely cannot grow.
//
// ⚠ WHICH KIND A FEATURE GETS IS DECLARED, NOT DECIDED PER PAGE. Both wrong answers hurt, in
// opposite directions: a page lock over a create-gated feature hides data the server is still
// serving and blocks edits it still accepts (22 of 23 gated cores gate `beforeCreate` only),
// while an add-only notice over a feature the account does not have at all shows a fully
// working page for something unusable. The declaration lives in the API's `appFeatures.ts`
// and reaches here generated — see `featureGateKind()` in `@/utils/planGating`.
//
// ⚠ IT ASKS `useFeatureGate`, NEVER `useApp().hasAppPlanFeature`. This component read the
// plan map directly until 2026-09-11, which is exactly what `useFeatureGate.ts`'s own header
// calls "the bug this prevents": a page that answers the question itself can never learn
// about an event-scoped grant when that half lands. It was the LAST direct reader left in the
// app, and it is the component we spread across every gated page — so the defect would have
// been multiplied by the very work that generalizes it.
//
// ⚠ THERE IS NO `forceEnabled`. A caller that computes its own boolean from a plan value is
// the same re-derivation by another name (`UserInvitationsPage` did, from `max_users_qty`).
// A numeric page lock passes `currentCount` and lets the one resolver evaluate the ceiling.
import React from "react";
import {
  Container,
  Paper,
  Stack,
  Title,
  Text,
  Button,
  Group,
  ThemeIcon,
  Box,
  Divider,
} from "@mantine/core";
import { IconLock, IconRocket } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { FEATURE_CODES } from "@/config";
import { FEATURE_DESCRIPTIONS } from "@/config/featureCodes.generated";

import { useApp } from "@/contexts/AppContext";
import { useFeatureGate } from "@/hooks/useFeatureGate";

import { FeatureGateKind, warnOnGateMismatch } from "@/utils/planGating";

import { isPremiumTrial, PremiumTrialMarker } from "../PlanFeatureNotice/PlanFeatureNotice";

export interface PremiumFeatureGateProps {
  /** Feature code to check against the user's plan */
  featureCode: keyof typeof FEATURE_CODES | string;

  /**
   * The event this page belongs to, when there is one.
   *
   * ⚠ Pass it even though the event half is not wired yet — it is carried straight into the
   * one resolver, so the page is already correct on the day `resolveEventGrant` answers.
   */
  eventId?: string | null;

  /**
   * For a NUMERIC feature: how many already exist, so the resolver can compare against the
   * plan's ceiling. `currentCount >= limit` locks, the same contract the server's gate uses —
   * so pass the count BEFORE the thing the page would add.
   */
  currentCount?: number | null;

  /** Title shown when feature is locked */
  featureTitle?: string;

  /**
   * What the feature gives you, shown under "What you'd get".
   *
   * ⚠ Defaults to the feature's own customer-facing description, generated from the API's
   * `appFeatures.ts`. A page bound in `siteMap.ts` therefore says something useful with no copy of
   * its own — which is what makes the sitemap binding a one-line change. Pass a richer node (a
   * `FeatureBenefitsList`) where the one-liner is not enough to earn the upgrade.
   */
  featureDescription?: React.ReactNode;

  /** The actual page content to render when feature is available */
  children: React.ReactNode;

  /**
   * Where the upgrade button goes. Defaults to `useFeatureGate().goToUpgrade` — on a partylabz event, that event's plans
   * (#60), else the Subscription page.
   */
  upgradeUrl?: string;

  /** Custom upgrade button text */
  upgradeButtonText?: string;

  /**
   * How the lock PRESENTS itself. The decision of whether to lock is never a variant —
   * that is `featureCode` + the resolver — so this only picks the chrome:
   *
   * - `page`   (default) a centred `Container` + `Paper`, for a whole route (`withPlanGate`).
   * - `panel`  the same content with NO container or border, for a surface that already has
   *   its own frame — a DRAWER body, or a section inside a page.
   * - `inline` a one-line `Paper` row with the button beside the text.
   *
   * ⚠ `panel` EXISTS BECAUSE OF A REAL COMPLAINT (owner, 2026-09-12). Price rules and coupon
   * grants used to REDIRECT a free host to the Subscription page the moment they clicked the
   * row action: the drawer they asked for never opened, and they landed on a billing screen
   * with no explanation of what they had just been refused. A host must be able to open the
   * thing, read what it does, and THEN decide to pay. Never answer a click with a redirect.
   */
  variant?: "page" | "panel" | "inline";

  /**
   * A deliberate departure from the feature's DECLARED gate kind, for the rare page that is
   * genuinely the exception.
   *
   * ⚠ It exists to be passed, not computed. A page may state "here this one is a whole-page
   * lock"; it may not derive that from a plan value — that is the re-derivation `forceEnabled`
   * used to be, and this prop is not a back door to it.
   */
  gate?: FeatureGateKind;

  /**
   * ROADMAP #238 — the page is an OUTPUT of the feature (a print, name badges): a draft or a test run does not open it.
   */
  output?: boolean;
}

export const PremiumFeatureGate: React.FC<PremiumFeatureGateProps> = ({
  featureCode,
  eventId,
  currentCount,
  featureTitle,
  featureDescription,
  children,
  upgradeUrl,
  upgradeButtonText,
  variant = "page",
  gate,
  output = false,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isInitializing } = useApp();
  const { checkFeature, goToUpgrade, upgradesEvent } = useFeatureGate();

  // #60 — never straight to the Subscription page: `goToUpgrade` knows when the fix is THIS event's upgrade (partylabz).
  const upgrade = upgradeUrl ? () => navigate(upgradeUrl) : goToUpgrade;

  // ⚠ The kill switch and ?paywalls=preview are handled INSIDE `checkFeature` — while the
  // gates are off it answers "allowed", because the server allows everything too and a lock
  // here would be a refusal nothing is actually making. This file no longer calls
  // `arePaywallsActive()` itself; that was one more place the answer could drift.
  const verdict = checkFeature(featureCode, { eventId, currentCount, output });

  // ⚠ Checks ITSELF against the declaration (ROADMAP A2.10): the whole-page lock is what a
  // feature declared `gate: 'page'` is supposed to get, and it is the harmful one to get wrong
  // — over a create-gated feature it hides records the server is still serving. Dev-only
  // console error; `npm run check-entitlement` catches the same thing statically.
  warnOnGateMismatch("PremiumFeatureGate", featureCode, gate);

  // While initializing, show nothing or a loader
  if (isInitializing) {
    return null;
  }

  // Feature is enabled - render children. ROADMAP #231 — a draft / test run using a premium page is told so above it.
  if (verdict.allowed) {
    return isPremiumTrial(verdict) ? (
      <>
        <PremiumTrialMarker verdict={verdict} onUpgrade={upgrade} />
        {children}
      </>
    ) : (
      <>{children}</>
    );
  }

  // Feature is locked - show upgrade prompt
  const title = featureTitle || verdict.featureName || t("premiumFeature.defaultTitle");

  // ⚠ THE SERVER'S OWN SENTENCE WHEN THERE IS ONE. `checkFeature` carries the refusal word for
  // word from the mirrored rule set, so a host reading this page and a host who hits the API
  // are told the same thing — including the ceiling ("your plan allows 1"), which the generic
  // copy cannot say. The translated line is the fallback, not the default.
  const reason = verdict.message || t("premiumFeature.notAvailableLong");

  // ⚠ The PANEL says "Subscribe", not "Upgrade Your Plan". It is shown to a host who opened a
  // drawer, not to one who went looking for billing — "Subscribe" names what the button does;
  // "Upgrade Your Plan" reads as though they already have one to upgrade.
  // #60 — on a partylabz event the button buys THIS event's plan, and says so.
  const buttonText =
    upgradeButtonText ??
    (upgradesEvent && !upgradeUrl
      ? t("premiumFeature.upgradeThisEvent", { defaultValue: "Upgrade this event" })
      : variant === "panel"
      ? t("premiumFeature.subscribe", { defaultValue: "Subscribe" })
      : t("premiumFeature.upgradeYourPlan"));

  // ⚠ The declared description is the FALLBACK, never an override — a page that passed its own
  // benefits list must keep it.
  const description = featureDescription ?? FEATURE_DESCRIPTIONS[featureCode] ?? null;

  if (variant === "inline") {
    return (
      <Paper p="md" radius="md" withBorder>
        <Group>
          <ThemeIcon size="lg" radius="xl" color="yellow" variant="light">
            <IconLock size={18} />
          </ThemeIcon>
          <Box style={{ flex: 1 }}>
            <Text fw={600}>{title}</Text>
            <Text size="sm" c="dimmed">
              {verdict.message || t("premiumFeature.notAvailableShort")}
            </Text>
          </Box>
          <Button
            variant="light"
            color="blue"
            size="md"
            leftSection={<IconRocket size={16} />}
            onClick={upgrade}
          >
            {buttonText}
          </Button>
        </Group>
      </Paper>
    );
  }

  // ⚠ NO Container and NO Paper — a drawer already IS the frame, and nesting a bordered card
  // inside one reads as a box in a box. Left-aligned for the same reason: a drawer body is a
  // column of content, not a landing page.
  if (variant === "panel") {
    return (
      <Stack gap="md">
        <Group gap="sm" wrap="nowrap" align="center">
          <ThemeIcon size={40} radius="xl" color="yellow" variant="light">
            <IconLock size={20} />
          </ThemeIcon>
          <Box style={{ flex: 1 }}>
            <Text fw={600} size="lg">
              {title}
            </Text>
            <Text size="md" c="dimmed">
              {reason}
            </Text>
          </Box>
        </Group>

        {description && (
          <>
            <Divider
              w="100%"
              label={t("premiumFeature.whatYouGet")}
              labelPosition="center"
            />
            <Box w="100%">{description}</Box>
          </>
        )}

        <Button
          size="md"
          leftSection={<IconRocket size={18} />}
          onClick={upgrade}
        >
          {buttonText}
        </Button>
      </Stack>
    );
  }

  return (
    <Container size="md" py="xl">
      <Paper p="xl" radius="md" withBorder>
        <Stack align="center" gap="lg">
          {/* Lock Icon */}
          <ThemeIcon size={64} radius="xl" color="yellow" variant="light">
            <IconLock size={32} />
          </ThemeIcon>

          {/* Title */}
          <Title order={2} ta="center">
            {title}
          </Title>

          {/* Subtitle */}
          <Text c="dimmed" ta="center" maw={500}>
            {reason}
          </Text>

          {/* Upgrade Button */}
          <Button
            size="lg"
            leftSection={<IconRocket size={20} />}
            onClick={upgrade}
          >
            {buttonText}
          </Button>

          {/* Feature Description */}
          {description && (
            <>
              <Divider
                w="100%"
                my="md"
                label={t("premiumFeature.whatYouGet")}
                labelPosition="center"
              />
              <Box w="100%">{description}</Box>
            </>
          )}
        </Stack>
      </Paper>
    </Container>
  );
};
